/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, inject, signal} from '@angular/core';
import * as _ from 'lodash';
import template from './site-group-edit-page.component.html';
import {SignalAdminResourceEditPageBase} from '../common/controller/signal-admin-resource-edit-page-base';
import {TeamApiService} from '../admin/team/team-api.service';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {deferSignalViewWrite} from '../common/util/template-state';
import {SiteGroupApiService, SiteGroupModel} from './site-group-api.service';
import {FormsModule} from '@angular/forms';
import {ButtonsModule} from 'ngx-bootstrap/buttons';
import {AppSharedModule} from '../common/app-shared.module';
import {FormControlsModule} from '../common/directives/form-controls/form-controls.module';

@Component({
  standalone: true,
  imports: [AppSharedModule, FormsModule, ButtonsModule, FormControlsModule],
  selector: 'adn-site-group-edit-page',
  template
})
export class SiteGroupEditPageComponent extends SignalAdminResourceEditPageBase<SiteGroupModel> {
  type: 'NETWORK' | 'TEAM' = 'NETWORK';
  teams: any[] | null = null;
  mpTeams: any[] = [];
  allMarketplacePublisherTeams: any[] = [];
  showOwnership = false;
  hasNetworkPermission = false;
  readonly viewRevision = signal(0);

  constructor(
    @Inject(LocalUserPermissions) private LocalUserPermissions: any,
    private readonly teamApi: TeamApiService
  ) {
    super(inject(SiteGroupApiService), 'siteGroup');
    this.hasNetworkPermission = LocalUserPermissions.hasNetworkPermission('MANAGE_SYSTEM');

    this.teamApi.listOptions().subscribe((page) => {
      // null → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferSignalViewWrite(this.viewRevision, () => {
        const allTeams = page.results;
        const isViewAllObjects = LocalUserPermissions.hasNetwork('VIEW_ALL_OBJECTS');
        this.teams = isViewAllObjects ? allTeams : _.filter(allTeams, (team: any) => {
          return LocalUserPermissions.isPartOf(team.network.tagId || team.network.id, team.id);
        });
        this.mpTeams = _.filter(this.teams, (team: any) => team.type !== 'AX_PUBLISHER');
        this.allMarketplacePublisherTeams = _.filter(this.teams, (team: any) => team.type === 'AX_PUBLISHER');
        this.showOwnership = this.allMarketplacePublisherTeams.length > 0;
        this.resolveOwnerTeamReference();
      });
    });
  }

  protected override afterEditingModelReset(): void {
    this.editingModel.teams = this.normalizeTeamIds(this.editingModel.teams);
    this.type = this.editingModel.ownerTeam ? 'TEAM' : 'NETWORK';
    this.resolveOwnerTeamReference();
  }

  protected override prepareForSubmit(model: SiteGroupModel): void {
    if (model.ownerTeam) {
      const validMpTeams = _.map(this.mpTeams, 'id');
      model.teams = _.filter(model.teams, (teamId: string) => {
        return validMpTeams.indexOf(teamId) > -1;
      });
    }
    if (this.type === 'NETWORK' || !model.ownerTeam) {
      model.ownerTeam = null;
      model.teams = [];
    }
  }

  onTypeChange(): void {
    if (this.type === 'NETWORK') {
      this.editingModel.ownerTeam = null;
      this.editingModel.teams = [];
    }
    this.markExternalFormDirty();
  }

  selectTeams(): void {
    this.editingModel.teams = _.map(this.mpTeams, 'id');
    this.markExternalFormDirty();
  }

  deselectTeams(): void {
    this.editingModel.teams = [];
    this.markExternalFormDirty();
  }

  isTeamSelected(teamId: string): boolean {
    return (this.editingModel.teams || []).indexOf(teamId) > -1;
  }

  toggleTeam(teamId: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const teams = [...(this.editingModel.teams || [])];
    const index = teams.indexOf(teamId);

    if (checked && index < 0) {
      teams.push(teamId);
    } else if (!checked && index > -1) {
      teams.splice(index, 1);
    }

    this.editingModel.teams = teams;
    this.markExternalFormDirty();
  }

  trackByTeamId(_index: number, team: any): string {
    return team.id;
  }

  private normalizeTeamIds(teams: any): string[] {
    return _.map(teams || [], (team: any) => _.isString(team) ? team : team.id);
  }

  private resolveOwnerTeamReference(): void {
    if (!this.editingModel?.ownerTeam || !this.allMarketplacePublisherTeams.length) {
      return;
    }
    const ownerTeamId = _.isString(this.editingModel.ownerTeam)
      ? this.editingModel.ownerTeam
      : this.editingModel.ownerTeam.id;
    const match = _.find(this.allMarketplacePublisherTeams, ['id', ownerTeamId]);
    if (match) {
      this.editingModel.ownerTeam = match;
    }
  }
}
